import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Building2, Check, Globe, Headphones, Loader2, Rocket, Store, Zap,
} from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = { kiosk: Store, storefront: Building2, department: Rocket, standalone: Globe };
const ORDER = ["kiosk", "storefront", "department"];

const KEYSTONE = [
    ["You get a white-label copy of this entire website.", "Every product, every live designer and every page carries your name."],
    ["Every other shop disappears.", "There is no map, no directory and no neighbors. Your customers only ever see you."],
    ["You get every live designer.", "White tees, colored apparel, DTF gang sheets and all 33 sign products, priced by you."],
    ["You pick what you print.", "Print the shelves you make yourself. Let 13thav print and ship the rest and keep 20% of those sales."],
    ["You can use your domain or ours.", "Start on yourname.13thav.com in five minutes, and point your own domain at it whenever you are ready."],
    ["We help you get open.", "We sit with you while you set it up, and we stay on call while you launch."],
];

export default function Spaces() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [cycle, setCycle] = useState("monthly");

    useEffect(() => { api.get("/stores/tiers").then(({ data }) => setData(data)).catch(() => { }); }, []);

    const tiers = data?.tiers || [];
    const vbm = tiers.find((t) => t.id === "standalone");
    const ladder = ORDER.map((id) => tiers.find((t) => t.id === id)).filter(Boolean);
    const priceOf = (t) => (cycle === "yearly" ? t.price_year : t.price_month);
    const suffix = cycle === "yearly" ? "/ yr" : "/ mo";
    const buyLink = (id) => (user ? `/account/sell?tier=${id}` : "/login");

    return (
        <Layout>
            <PageIntro trail={[{ label: "Plans" }]} kicker="Plans · four ways to be on 13thav" title="Plans & spaces." testid="spaces-intro"
                description="Four plans, one software. Three of them list your shop in the 13thav directory next to the other shops; the fourth is a private white-label website of your own. Every plan uses the same catalog, live designers and back office. Pick the one that fits, and change it any time." />
            {/* ---------------- the keystone ---------------- */}
            <section className="relative overflow-hidden bg-[#0B0B0B]" data-testid="spaces-hero">
                <div className="pointer-events-none absolute inset-0 opacity-[0.35]"
                    style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "100% 48px" }} />
                <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300"
                        data-testid="spaces-badge">
                        <Globe size={11} /> The keystone · Virtual Brick & Mortar
                    </span>
                    <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[0.92] tracking-tighter text-white sm:text-5xl lg:text-6xl">
                        TAKE THIS WHOLE<br />WEBSITE. PUT YOUR<br />NAME ON IT.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base text-zinc-300 md:text-lg">
                        Virtual Brick & Mortar is a white-label copy of this whole website: the catalog, the live
                        designers, the checkout and the back office. The directory disappears. It is your store, your
                        prices and your customers, and you can be selling in five minutes.
                    </p>

                    <div className="mt-8 grid gap-x-8 gap-y-4 md:grid-cols-2" data-testid="spaces-vbm-list">
                        {KEYSTONE.map(([title, copy]) => (
                            <div key={title} className="border-t border-white/15 pt-3.5">
                                <p className="flex items-start gap-2 font-display text-base font-bold text-white">
                                    <Check size={16} className="mt-0.5 shrink-0 text-[#2563EB]" /> {title}
                                </p>
                                <p className="mt-1 pl-6 text-sm text-zinc-400">{copy}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-9 flex flex-wrap items-center gap-5">
                        <div>
                            <p className="font-display text-4xl font-black tracking-tight text-white" data-testid="spaces-vbm-price">
                                ${(vbm?.price_month ?? 299).toLocaleString()}
                                <span className="text-base font-bold text-zinc-400"> / mo</span>
                            </p>
                            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                                or ${(vbm?.price_year ?? 1990).toLocaleString()} a year — keep ${(((vbm?.price_month ?? 299) * 12) - (vbm?.price_year ?? 1990)).toLocaleString()}
                            </p>
                        </div>
                        <Link to={buyLink("standalone")} data-testid="spaces-vbm-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:scale-[1.03]">
                            <Rocket size={17} /> Open my own site
                        </Link>
                        <Link to="/stores/the-sign-shop" data-testid="spaces-vbm-demo"
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-white">
                            See a live shop page <ArrowRight size={15} />
                        </Link>
                        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                            <Headphones size={12} /> dedicated support while you launch
                        </span>
                    </div>
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                        50% off your first month · cancel any time · 13thav keeps {data?.fee_pct ?? 1}% of card sales in the directory and 0% on a private site
                    </p>
                </div>
            </section>

            {/* ---------------- the ladder ---------------- */}
            <section className="border-b border-zinc-200 bg-white" data-testid="spaces-ladder">
                <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600">The ladder</p>
                            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tighter text-zinc-900 sm:text-4xl">
                                OR JOIN THE DIRECTORY.
                            </h2>
                            <p className="mt-3 max-w-2xl text-sm text-zinc-700 md:text-base">
                                Every rung buys you more of the same machine. Start at a cart in the center court and walk
                                up to the anchor store — or skip the ladder and take the whole building with Virtual
                                Brick & Mortar above.
                            </p>
                        </div>
                        <div className="flex gap-1.5" data-testid="spaces-cycle">
                            {[["monthly", "Monthly"], ["yearly", "Yearly · save"]].map(([key, label]) => (
                                <button key={key} onClick={() => setCycle(key)} data-testid={`spaces-cycle-${key}`}
                                    className={`rounded-full border px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] ${cycle === key
                                        ? "border-zinc-900 bg-zinc-800 text-zinc-50" : "border-zinc-300 bg-white text-zinc-800 hover:border-zinc-900"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {!data ? (
                        <div className="flex justify-center py-14"><Loader2 className="animate-spin text-zinc-500" /></div>
                    ) : (
                        <div className="mt-8 grid gap-4 lg:grid-cols-3" data-testid="spaces-tiers">
                            {ladder.map((t, i) => {
                                const Icon = ICONS[t.id] || Store;
                                return (
                                    <div key={t.id} data-testid={`spaces-tier-${t.id}`}
                                        className="flex flex-col rounded-2xl border border-zinc-200 bg-white p-5 transition-shadow hover:shadow-[0_18px_40px_-28px_rgba(0,0,0,0.45)]">
                                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                                            Rung {i + 1} of 3
                                        </p>
                                        <p className="mt-2 flex items-center gap-2 font-display text-xl font-black tracking-tight text-zinc-900">
                                            <Icon size={18} /> {t.name}
                                        </p>
                                        <p className="mt-1 text-sm text-zinc-700">{t.tagline}</p>
                                        <p className="mt-4 font-display text-3xl font-black tracking-tight text-zinc-900">
                                            {priceOf(t) ? `$${priceOf(t).toLocaleString()}` : "Free"}
                                            {!!priceOf(t) && <span className="text-sm font-bold text-zinc-600"> {suffix}</span>}
                                        </p>
                                        {t.id === "department" && t.price_lifetime && (
                                            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-600">
                                                or ${t.price_lifetime.toLocaleString()} once — keep the designers forever
                                            </p>
                                        )}
                                        <ul className="mt-4 flex-1 space-y-1.5">
                                            {t.perks.slice(0, 5).map((p) => (
                                                <li key={p} className="flex gap-2 text-sm text-zinc-800">
                                                    <Check size={14} className="mt-0.5 shrink-0 text-zinc-500" /> {p}
                                                </li>
                                            ))}
                                        </ul>
                                        <dl className="mt-4 space-y-1 border-t border-zinc-200 pt-3">
                                            {[["Live ads", t.listing_cap ? `${t.listing_cap} at a time` : "Unlimited"],
                                            ["Live designers", t.designers ? "All four" : "Not included"],
                                            ["Store page", t.store_page === "full" ? "Full page" : "Starter profile"],
                                            ["Opens", t.needs_review ? "After a quick review" : "Right away"]].map(([k, v]) => (
                                                <div key={k} className="flex items-baseline justify-between gap-3">
                                                    <dt className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-600">{k}</dt>
                                                    <dd className="text-xs font-semibold text-zinc-900">{v}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                        <Link to={buyLink(t.id)} data-testid={`spaces-open-${t.id}`}
                                            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-zinc-900 bg-zinc-900 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-white hover:bg-black">
                                            Open a {t.name} <ArrowRight size={12} />
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-5 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-zinc-900">
                            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900">
                                <Zap size={13} /> Not sure yet?
                            </span>
                            <span className="ml-2">
                                Every plan is half price for your first month and you can move up or down whenever you want. A Starter today
                                can be a Pro Shop next month with one click.
                            </span>
                        </p>
                        <div className="flex shrink-0 flex-wrap gap-2">
                            <Link to="/directory" data-testid="spaces-see-floor"
                                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800 hover:border-zinc-900">
                                Browse the directory
                            </Link>
                            <Link to={user ? "/account/sell" : "/register"} data-testid="spaces-start"
                                className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-white hover:bg-black">
                                Start now
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
